import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import {
  ArrowLeft,
  PackageCheck,
  Plus,
  Trash2,
  FileText,
  Building2,
  Calendar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormDrawer } from "@/components/ui/form-drawer";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface Catalog {
  id: string;
  vendorId: string | null;
  name: string;
  description: string | null;
  status: string;
  currency: string | null;
  validFrom: string | null;
  validTo: string | null;
  fileName: string | null;
  fileUrl: string | null;
  fileSize: number | null;
  notes: string | null;
  createdAt: string;
  itemsCount: number;
  vendor?: { id: string; name: string; code: string | null };
}

interface CatalogItem {
  id: string;
  catalogId: string;
  productId: string | null;
  sku: string | null;
  description: string;
  uom: string | null;
  unitPrice: string | null;
  minimumOrderQuantity: string | null;
  leadTimeDays: number | null;
  notes: string | null;
  product?: { id: string; name: string; code: string };
}

interface CatalogResponse {
  catalog: Catalog;
  items: CatalogItem[];
}

const itemSchema = z.object({
  sku: z.string().optional(),
  description: z.string().min(1, "Description required"),
  uom: z.string().optional(),
  unitPrice: z.string().optional(),
  minimumOrderQuantity: z.string().optional(),
  leadTimeDays: z.string().optional(),
  notes: z.string().optional(),
});

type ItemFormData = z.infer<typeof itemSchema>;

const STATUSES = ["draft", "active", "archived"];

function CatalogStatusBadge({ status }: { status: string }) {
  const cls =
    status === "active"
      ? "bg-green-600 text-white"
      : status === "draft"
      ? "bg-gray-500 text-white"
      : "bg-gray-400 text-white";
  return <Badge className={cls}>{status}</Badge>;
}

export default function VendorPortalCatalogDetailPage() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { sessionId } = useAuth();
  const [addItemOpen, setAddItemOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<CatalogResponse>({
    queryKey: ["/api/vendor-portal/catalogs", params.id],
    queryFn: () =>
      api.get<CatalogResponse>(
        `/vendor-portal/catalogs/${params.id}`,
        sessionId,
      ),
    enabled: !!params.id,
  });

  const form = useForm<ItemFormData>({
    resolver: zodResolver(itemSchema),
    defaultValues: { description: "", uom: "each" },
  });

  const addItemMutation = useMutation({
    mutationFn: async (formData: ItemFormData) =>
      api.post(
        `/vendor-portal/catalogs/${params.id}/items`,
        {
          ...formData,
          leadTimeDays: formData.leadTimeDays ? Number(formData.leadTimeDays) : undefined,
        },
        sessionId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/vendor-portal/catalogs", params.id],
      });
      toast({ title: "Item added" });
      setAddItemOpen(false);
      form.reset({ description: "", uom: "each" });
    },
    onError: (e: Error) =>
      toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteItemMutation = useMutation({
    mutationFn: async (itemId: string) =>
      api.delete(
        `/vendor-portal/catalogs/${params.id}/items/${itemId}`,
        sessionId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/vendor-portal/catalogs", params.id],
      });
      toast({ title: "Item removed" });
    },
    onError: (e: Error) =>
      toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus: string) =>
      api.patch(
        `/vendor-portal/catalogs/${params.id}`,
        { status: newStatus },
        sessionId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/vendor-portal/catalogs", params.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["vendor-portal-catalogs"],
      });
      toast({ title: "Status updated" });
    },
    onError: (e: Error) =>
      toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }

  if (!data?.catalog) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Catalog not found</p>
      </div>
    );
  }

  const { catalog, items } = data;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setLocation("/vendor-portal/catalogs")}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-2xl font-bold tracking-tight">{catalog.name}</h1>
            <CatalogStatusBadge status={catalog.status} />
          </div>
          {catalog.description && (
            <p className="text-sm text-muted-foreground">{catalog.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={catalog.status}
            onValueChange={(v) => updateStatusMutation.mutate(v)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => setAddItemOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Item
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="h-4 w-4" />
              Vendor
            </CardTitle>
          </CardHeader>
          <CardContent>
            {catalog.vendor ? (
              <div>
                <p className="text-sm font-medium">{catalog.vendor.name}</p>
                {catalog.vendor.code && (
                  <p className="text-xs text-muted-foreground">
                    {catalog.vendor.code}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No vendor assigned</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Calendar className="h-4 w-4" />
              Validity Period
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <p className="text-xs text-muted-foreground">From</p>
              <p className="text-sm font-medium">
                {catalog.validFrom
                  ? format(new Date(catalog.validFrom), "MMM d, yyyy")
                  : "—"}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">To</p>
              <p className="text-sm font-medium">
                {catalog.validTo
                  ? format(new Date(catalog.validTo), "MMM d, yyyy")
                  : "—"}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4" />
              File
            </CardTitle>
          </CardHeader>
          <CardContent>
            {catalog.fileName ? (
              <div>
                <p className="text-sm font-medium">{catalog.fileName}</p>
                {catalog.fileSize != null && (
                  <p className="text-xs text-muted-foreground">
                    {(catalog.fileSize / 1024).toFixed(1)} KB
                  </p>
                )}
                {catalog.fileUrl && (
                  <a
                    href={catalog.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Download
                  </a>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No file uploaded</p>
            )}
            <div className="mt-3 pt-3 border-t">
              <p className="text-xs text-muted-foreground">Currency</p>
              <p className="text-sm font-medium">{catalog.currency || "USD"}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <PackageCheck className="h-4 w-4" />
            Catalog Items ({items.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <PackageCheck className="h-12 w-12 text-muted-foreground/50 mx-auto mb-2" />
              <p className="text-muted-foreground">No items in this catalog yet</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setAddItemOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add First Item
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>UOM</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead className="text-right">MOQ</TableHead>
                  <TableHead className="text-right">Lead Time (days)</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-mono text-xs">
                      {item.sku || item.product?.code || "—"}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{item.description}</div>
                      {item.product && (
                        <div className="text-xs text-muted-foreground">
                          {item.product.name}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{item.uom || "each"}</TableCell>
                    <TableCell className="text-right">
                      {item.unitPrice
                        ? `$${Number(item.unitPrice).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}`
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {item.minimumOrderQuantity || "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {item.leadTimeDays ?? "—"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive"
                        onClick={() => {
                          if (
                            confirm("Remove this item from the catalog?")
                          ) {
                            deleteItemMutation.mutate(item.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <FormDrawer
        open={addItemOpen}
        onOpenChange={setAddItemOpen}
        title="Add Catalog Item"
        description="Add a product line to this vendor catalog."
        formId="add-catalog-item"
        isPending={addItemMutation.isPending}
        submitLabel="Add Item"
        size="md"
        data-testid="drawer-add-catalog-item"
      >
        <Form {...form}>
          <form
            id="add-catalog-item"
            onSubmit={form.handleSubmit((d) => addItemMutation.mutate(d))}
            className="space-y-6"
          >
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="sku"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SKU</FormLabel>
                    <FormControl>
                      <Input variant="flat" placeholder="VEN-001" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="uom"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>UOM</FormLabel>
                    <FormControl>
                      <Input variant="flat" placeholder="each" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description *</FormLabel>
                  <FormControl>
                    <Input variant="flat" placeholder="Product description" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="unitPrice"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unit Price</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                          $
                        </span>
                        <Input
                          variant="flat"
                          type="number"
                          step="0.01"
                          className="pl-7"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="minimumOrderQuantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Min Order Qty</FormLabel>
                    <FormControl>
                      <Input variant="flat" type="number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="leadTimeDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Lead Time (days)</FormLabel>
                    <FormControl>
                      <Input variant="flat" type="number" min="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea variant="flat" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </FormDrawer>
    </div>
  );
}
